/**
 * ContactEmailComposer — send an email to a contact with templates, signatures,
 * and the shared AIEmailWriter panel. Pulls the contact MD file under the hood
 * (via /api/ai/write-email) so AI drafts are grounded in the contact's
 * meeting history and analysis.
 *
 * ## The From picker (issue #484)
 *
 * This is a PERSONAL-lane surface: it sends through the user's own connected
 * Gmail or Outlook mailbox, not through a departmental unified inbox. Which
 * mailbox sends used to be inferred from a hardcoded Gmail-first precedence,
 * stated twice — once in the routing and once in this component's status line —
 * so a user with both mailboxes connected was never offered the choice and the
 * UI could assert Gmail while Outlook sent. `usePersonalSenders` is now the
 * single source of truth for both, and the confirmation names the address that
 * actually sent (`res.sentVia`), not the one that was selected.
 *
 * A dead "unified-inbox mode" lived here until #484 — three optional props
 * (`fromOptions`, `onSendOverride`, `sendContextLabel`) that no caller ever
 * passed. It was an abandoned attempt to make this personal-lane component
 * serve the departmental one; the departmental lane got its own full-page
 * routes instead (see UnifiedInboxSurface) and the scaffolding was left behind.
 * It has been removed rather than dual-pathed.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import RichTextEditor from './RichTextEditor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import {
    Mail, Send, Loader2, Sparkles, FileText, PenLine,
} from 'lucide-react';
import AIEmailWriter from './AIEmailWriter';
import ReplyAsSelector from './inbox/ReplyAsSelector';
import { useSendEmail, useEmailTemplates, useSignatures } from '../hooks/useInbox';
import { useAuth } from '../context/AuthContext';
import ProviderBadge from './inbox/ProviderBadge';
import { usePersonalSenders } from '../hooks/usePersonalSenders';
import { resolveSigVars } from '../lib/signatureMergeVars';
import {
    buildSignatureHtml, hasAuthoredText, replaceSignatureBlock, signatureBlockId,
} from '../lib/editorSafeHtml';
import { isOrgOverride, pickSeedSignature } from '../lib/signatureSelection';
import { buildContactMergeMap, resolveMergeVars } from '../lib/mergeVars';
import { isSharedWithMe, sharedItemLabel, splitByOwner } from '../lib/sharedItems';

const EMPTY_EVENT_OPTIONS = [];


export default function ContactEmailComposer({
    open,
    onOpenChange,
    contact,
    account,
    deal,
    onSent,
    eventOptions = EMPTY_EVENT_OPTIONS,
}) {
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [templateId, setTemplateId] = useState('none');
    const [signatureId, setSignatureId] = useState('');
    const [selectedEventId, setSelectedEventId] = useState('');

    const [showAI, setShowAI] = useState(false);
    const {
        options, provider, selectByOptionValue, selected, hasAnySender, sendFlags,
        isLoading: sendersLoading,
    } = usePersonalSenders();
    const sendEmailMutation = useSendEmail();
    const sending = sendEmailMutation.isPending;
    const toAddress = contact?.email;

    useEffect(() => {
        if (!open) return;
        setSelectedEventId(eventOptions.length === 1 ? eventOptions[0].event_id : '');
    }, [open, eventOptions]);


    // The PascalCase vocabulary MergeVariableDropdown inserts and
    // backend/utils/merge_fields.py resolves. This map used to be keyed
    // snake_case (contact_first_name, company_name, …), which matched NONE of
    // the tokens a user can actually put in a template — so a template loaded
    // here kept its literal {{FirstName}} and, because the contact send lane
    // resolved nothing server-side either, the recipient received it verbatim.
    const contactMergeMap = useMemo(
        () => buildContactMergeMap(contact, account),
        [contact, account],
    );

    const { user } = useAuth();
    const { data: templatesData } = useEmailTemplates(open);
    const { data: signaturesData } = useSignatures(open);
    const personalTemplates = templatesData?.templates || [];
    const adminTemplates = templatesData?.adminTemplates || [];
    const personalSignatures = signaturesData?.signatures || [];
    const adminSignatures = signaturesData?.adminSignatures || [];
    // #955: personal lists also carry items shared with this user; grouped apart.
    const ownTemplates = splitByOwner(personalTemplates, user?.user_id);
    const ownSignatures = splitByOwner(personalSignatures, user?.user_id);
    const templates = useMemo(
        () => [...(templatesData?.templates || []), ...(templatesData?.adminTemplates || [])],
        [templatesData]
    );
    const signatures = useMemo(
        () => [...(signaturesData?.signatures || []), ...(signaturesData?.adminSignatures || [])],
        [signaturesData]
    );

    // The default signature comes from the shared /api/signatures fetch the
    // composer already performs via useSignatures (see the hook above).
    // #955: never seed a signature a colleague shared — only the user's own or
    // the org's; a shared one is picked by hand.
    const seedableSignatures = useMemo(
        () => signatures.filter(s => !isSharedWithMe(s, user?.user_id)),
        [signatures, user?.user_id]
    );
    const defaultSignature = useMemo(
        () => seedableSignatures.find(s => s.is_default) || seedableSignatures[0] || null,
        [seedableSignatures]
    );
    // #955: the org-wide signature (active_override_id) wins over the user's
    // default — the same precedence every inbox composer reads from
    // pickSeedSignature (#597). Preselected, never locked: None stays available.
    // Both the dropdown and the body seed from this ONE value so they agree.
    const seedSignature = useMemo(
        () => pickSeedSignature({
            activeOverrideId: signaturesData?.activeOverrideId,
            signatures: signaturesData?.signatures,
            adminSignatures: signaturesData?.adminSignatures,
            defaultSignature,
        }),
        [signaturesData, defaultSignature]
    );
    // Labelled while the org override is the selected signature — same rule
    // and copy as TemplateSignatureSelectors.
    const overrideActive = isOrgOverride(
        signatures.find(s => s.signature_id === signatureId),
        signaturesData?.activeOverrideId,
    );

    const appliedSigForOpen = useRef(null);
    useEffect(() => {
        if (!open) { appliedSigForOpen.current = null; return; }
        if (appliedSigForOpen.current) return;
        // Reopened over a kept draft: show the signature the body really
        // holds (or None), not the seed — the auto-insert below only runs on
        // an empty body, so the two would otherwise disagree.
        if (body) {
            const inBody = signatureBlockId(body);
            setSignatureId(inBody || '');
            appliedSigForOpen.current = inBody || 'none';
            return;
        }
        const def = seedSignature;
        if (def) {
            setSignatureId(def.signature_id);
            appliedSigForOpen.current = def.signature_id;
        }
    }, [open, seedSignature, signatures, body]);

    // #487 — the address this message will actually be sent from. The CRM
    // login email is used ONLY when no mailbox can send. A connected mailbox
    // whose address we cannot read merges an EMPTY string rather than the
    // login email: {{SenderEmail}} is baked into the body of a message that
    // really goes out, so a wrong address there is worse than a missing one.
    const senderEmailVar = selected?.address || (hasAnySender ? '' : (user?.email || ''));

    // Explicit deps are load-bearing (see lib/signatureMergeVars.js): a
    // memoized insertSignature would otherwise capture the FIRST render's
    // closure — before the mailbox status resolves — and bake the CRM login
    // email into every inserted signature, which is exactly the #487 bug.
    const resolveForThisComposer = useCallback(
        (html) => resolveSigVars(html, user, senderEmailVar),
        [user, senderEmailVar]
    );

    // A user-authored template can carry all three token families the
    // MergeVariableDropdown offers — contact, account and sender. Resolve
    // every family in one pass so what the composer shows is what the
    // recipient gets. Previously only the SIGNATURE was sender-resolved and
    // the template body was resolved against the wrong vocabulary, so a
    // template's {{FirstName}} and {{SenderName}} both went out literal.
    const resolveTemplateVars = useCallback(
        (text) => resolveForThisComposer(resolveMergeVars(text, contactMergeMap)),
        [resolveForThisComposer, contactMergeMap],
    );

    // #970 — the signature is one block that keeps its layout
    // (buildSignatureHtml, shared with every composer). Replace-not-stack finds
    // that block in the body, so there is nothing to track here. The updater is
    // pure: StrictMode runs it twice, and the old version mutated a ref inside
    // it, which is what stacked signatures and made None remove nothing.
    const signatureBlockFor = useCallback(
        (signature) => (signature ? buildSignatureHtml(signature, user, senderEmailVar) : ''),
        [user, senderEmailVar],
    );

    const insertSignature = useCallback((signature) => {
        const block = signatureBlockFor(signature);
        setBody(prev => replaceSignatureBlock(prev, block)); // '' → None: removes the block only
    }, [signatureBlockFor]);

    const applyTemplate = useCallback((id) => {
        setTemplateId(id);
        if (id === 'none') return;
        const t = templates.find(t => t.template_id === id || t.id === id);
        if (!t) return;
        if (t.subject) setSubject(resolveTemplateVars(t.subject));
        if (t.body || t.body_html) {
            const tplHtml = resolveTemplateVars(t.body || t.body_html);
            const sigHtml = signatureBlockFor(signatures.find(sg => sg.signature_id === signatureId));
            setBody(sigHtml ? `${tplHtml}<p></p>${sigHtml}` : tplHtml);
        }
    }, [templates, resolveTemplateVars, signatures, signatureId, signatureBlockFor]);

    const handleAIInsert = useCallback((content) => {
        // AIEmailWriter returns an object with {subject, body} or raw string depending on API.
        if (content && typeof content === 'object') {
            if (content.subject) setSubject(resolveTemplateVars(content.subject));
            if (content.body) {
                // Plain text collapses into one run-on paragraph in the HTML
                // editor — wrap it so newlines survive. HTML bodies pass through.
                const html = /<[a-z][\s\S]*>/i.test(content.body)
                    ? content.body
                    : `<p>${content.body.replace(/\n{2,}/g, '</p><p>').replace(/\n/g, '<br/>')}</p>`;
                // The writer is prompted with the merge vocabulary, so its
                // output can contain tokens too.
                setBody(resolveTemplateVars(html));
            }
        } else if (typeof content === 'string') {
            setBody(resolveTemplateVars(content));
        }
        setShowAI(false);
    }, [resolveTemplateVars]);

    // Wires the dropdown to the new insert action: selecting a signature
    // inserts it into the body (resolved); 'none' removes the inserted one.
    // useCallback for consistency with applyTemplate/insertSignature above.
    const onSignatureChange = useCallback((id) => {
        setSignatureId(id);
        // Remember the user's pick for this open, so the auto-insert below
        // never puts the seed back after they chose None.
        appliedSigForOpen.current = id && id !== 'none' ? id : 'none';
        const sig = signatures.find(sg => sg.signature_id === id) || null;
        insertSignature(sig); // null → removes the inserted signature
    }, [signatures, insertSignature]);

    // Auto-load the seed signature (org override, else the user's default) on
    // open, only when the body is empty — never wipe user content. It comes
    // from the same /api/signatures fetch the composer already performs.
    //
    // Deps include signatureBlockFor and the effect gates on
    // !sendersLoading — both load-bearing (audit fix for a #487-class race):
    // if /api/signatures resolved BEFORE the mailbox status queries, the old
    // [open, defaultSignature] deps let insertSignature run with the resolver
    // still closed over the CRM-login-email fallback, baking the login email
    // into {{SenderEmail}} of the auto-inserted signature. Mirrors
    // PersonalComposePage's auto-load effect, which has the same gate.
    useEffect(() => {
        if (open && !sendersLoading && seedSignature && !body && appliedSigForOpen.current !== 'none') {
            insertSignature(seedSignature);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, seedSignature, signatureBlockFor, sendersLoading]);

    const authored = hasAuthoredText(body);

    const send = async () => {
        if (!toAddress) { toast.error('Contact has no email'); return; }
        if (!subject.trim()) { toast.error('Subject required'); return; }
        if (!hasAuthoredText(body)) { toast.error('Type a message first'); return; }
        if (eventOptions.length > 1 && !selectedEventId) {
            toast.error('Choose which event this email is following up on');
            return;
        }
        try {
            // The signature is already inside the body (insertSignature put it
            // there, merge vars resolved) — send the editor HTML as-is. The old
            // newline-to-<br/> conversion would double-escape TipTap markup, and
            // the separate sigHtml append is gone with it.
            const html = body;

            const res = await sendEmailMutation.mutateAsync({
                to: contact.email,
                subject,
                body: html,
                // #484 — the intent. `useSendEmail` still falls back to the old
                // Gmail-first precedence if this mailbox went away mid-compose.
                provider,
                // Connected AND mail auth intact — derived once in
                // usePersonalSenders, not restated here. A mailbox flagged for
                // re-auth used to route to its provider and surface the
                // server's explicit "reconnect" error; it now falls through,
                // which is why the disabled "Reconnect required" row and the
                // no-sender hint below are not optional.
                ...sendFlags,
                contact_id: contact.contact_id,
                ...(account?.account_id && { account_id: account.account_id }),
            });

            // Name the address that ACTUALLY sent. `sentVia` differs from
            // `provider` when the selected mailbox went away mid-compose.
            const usedAddress =
                options.find(o => o.provider === res?.sentVia)?.address || selected?.address;
            toast.success(
                usedAddress
                    ? `Email sent from ${usedAddress}`
                    : 'Email logged (connect Gmail or Outlook to send directly)'
            );

            await onSent?.({ eventId: selectedEventId || null, subject });
            onOpenChange(false);
            setSubject('');
            setBody('');
            setTemplateId('none');
            // Reset the signature selection so the next open starts clean.
            // Without this, the select would still display the previously
            // chosen signature while the auto-load effect (body is now empty)
            // inserts the SEED one — select value and in-body signature
            // disagree until the user re-picks.
            setSignatureId('');
        } catch (err) {
            // Map backend detail codes to human copy rather than surfacing the
            // raw code (e.g. a literal 'send_timeout'). Mirrors the mapping
            // PersonalComposePage already applies on its send path.
            const detail = err.response?.data?.detail;
            toast.error(
                detail === 'recipient_required' ? 'Please add at least one recipient'
                    : detail === 'send_timeout' ? 'The email server took too long to respond. Please try again.'
                    : detail === 'send_failed' ? 'Failed to send email. The mail server may be busy — please try again in a moment.'
                    : detail || 'Failed to send email'
            );
        }
    };

    if (showAI) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-violet-600" /> AI email writer
                        </DialogTitle>
                    </DialogHeader>
                    <AIEmailWriter
                        contactId={contact?.contact_id}
                        dealId={deal?.deal_id}
                        accountId={account?.account_id}
                        existingDraft={body ? `Subject: ${subject}\n\n${body}` : null}
                        onInsert={handleAIInsert}
                        onClose={() => setShowAI(false)}
                    />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-email-composer">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {`Email ${contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : ''}`}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-3">
                    {/* #484 — the From picker replaces the old "Sending via …"
                        status line, which restated the routing precedence in
                        the UI and could therefore disagree with it. All options
                        are passed, not just the sendable ones: a mailbox that
                        is connected but whose auth has broken must render as a
                        disabled "Reconnect required" row. Passing [] here would
                        show "No mailbox connected" to a user who plainly has
                        one connected — a worse lie than the one this fixes. */}
                    <ReplyAsSelector
                        label="From"
                        options={options}
                        value={provider}
                        onChange={selectByOptionValue}
                        singleHint="This is the only mailbox you have connected."
                        // Until the two status queries resolve there are no
                        // options, and "No mailbox connected" would be a claim
                        // the composer cannot yet make.
                        emptyHint={sendersLoading ? 'Checking connected mailboxes…' : 'No mailbox connected'}
                        renderBadge={(opt) => <ProviderBadge provider={opt.provider} />}
                    />

                    <div className="text-xs text-stone-500">
                        To: <span className="font-medium text-stone-700">{contact?.email || '(no email on contact)'}</span>
                    </div>


                    {eventOptions.length > 0 && (
                        <div>
                            <Label className="text-xs">Event follow-up</Label>
                            <select
                                value={selectedEventId}
                                onChange={e => setSelectedEventId(e.target.value)}
                                className="mt-1 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm"
                                data-testid="email-event-select"
                            >
                                {eventOptions.length > 1 && <option value="">— Choose an event —</option>}
                                {eventOptions.map(event => (
                                    <option key={event.event_id} value={event.event_id}>
                                        {event.name || event.title || event.event_id}
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1 text-[11px] text-stone-500">
                                The email will be recorded against the selected event and advance its follow-up stage.
                            </p>
                        </div>
                    )}


                    {!sendersLoading && !hasAnySender && (
                        <div className="inline-flex items-center gap-1 text-xs text-amber-600">
                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                            No email provider connected — this will only log a CRM communication.
                            <a href="/settings?tab=integrations" className="underline">Settings → Integrations</a>
                        </div>
                    )}
                    {selected?.warning === 'no_calendar' && (
                        <p className="text-[10px] text-stone-400">
                            Calendar is not connected for this mailbox — sending is unaffected.
                        </p>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs flex items-center gap-1">
                                <FileText className="w-3 h-3" /> Template
                            </Label>
                            <Select value={templateId} onValueChange={applyTemplate}>
                                <SelectTrigger data-testid="email-template-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None (custom)</SelectItem>
                                    {ownTemplates.mine.length > 0 && (
                                        <>
                                            <div className="px-2 py-1 text-xs font-semibold text-stone-400">My Templates</div>
                                            {ownTemplates.mine.map(t => (
                                                <SelectItem key={t.template_id || t.id} value={t.template_id || t.id}>
                                                    {t.name || t.title}
                                                </SelectItem>
                                            ))}
                                        </>
                                    )}
                                    {ownTemplates.shared.length > 0 && (
                                        <>
                                            <div className="px-2 py-1 text-xs font-semibold text-stone-400 border-t mt-1 pt-1" data-testid="email-template-shared-group">Shared with me</div>
                                            {ownTemplates.shared.map(t => (
                                                <SelectItem key={t.template_id || t.id} value={t.template_id || t.id}>
                                                    {sharedItemLabel(t.name || t.title, t)}
                                                </SelectItem>
                                            ))}
                                        </>
                                    )}
                                    {adminTemplates.length > 0 && (
                                        <>
                                            <div className="px-2 py-1 text-xs font-semibold text-stone-400 border-t mt-1 pt-1">Org Templates</div>
                                            {adminTemplates.map(t => (
                                                <SelectItem key={t.template_id || t.id} value={t.template_id || t.id}>
                                                    {t.name || t.title}
                                                </SelectItem>
                                            ))}
                                        </>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs flex items-center gap-1">
                                <PenLine className="w-3 h-3" /> Signature
                                {overrideActive && (
                                    <span
                                        className="text-[10px] font-normal text-stone-400"
                                        data-testid="signature-org-override-note-contact"
                                    >
                                        · Set by your organisation
                                    </span>
                                )}
                            </Label>
                            <Select value={signatureId || 'none'} onValueChange={onSignatureChange}>
                                <SelectTrigger data-testid="email-signature-select"><SelectValue placeholder="None" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    {ownSignatures.mine.length > 0 && (
                                        <>
                                            <div className="px-2 py-1 text-xs font-semibold text-stone-400">My Signatures</div>
                                            {ownSignatures.mine.map(s => (
                                                <SelectItem key={s.signature_id} value={s.signature_id}>
                                                    {s.name}{s.is_default ? ' (default)' : ''}
                                                </SelectItem>
                                            ))}
                                        </>
                                    )}
                                    {ownSignatures.shared.length > 0 && (
                                        <>
                                            <div className="px-2 py-1 text-xs font-semibold text-stone-400 border-t mt-1 pt-1" data-testid="email-signature-shared-group">Shared with me</div>
                                            {ownSignatures.shared.map(s => (
                                                <SelectItem key={s.signature_id} value={s.signature_id}>
                                                    {sharedItemLabel(s.name, s)}
                                                </SelectItem>
                                            ))}
                                        </>
                                    )}
                                    {adminSignatures.length > 0 && (
                                        <>
                                            <div className="px-2 py-1 text-xs font-semibold text-stone-400 border-t mt-1 pt-1">Org Signatures</div>
                                            {adminSignatures.map(s => (
                                                <SelectItem key={s.signature_id} value={s.signature_id}>
                                                    {s.name}{s.is_default ? ' (default)' : ''}
                                                </SelectItem>
                                            ))}
                                        </>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs">Subject</Label>
                        <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject…" />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs">Body</Label>
                            <button
                                type="button"
                                onClick={() => setShowAI(true)}
                                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-violet-50 text-violet-700 hover:bg-violet-100"
                                data-testid="ai-writer-button"
                            >
                                <Sparkles className="w-3 h-3" /> AI writer
                            </button>
                        </div>
                        <RichTextEditor
                            content={body}
                            onChange={setBody}
                            placeholder="Write your email…"
                            minHeight="300px"
                        />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-stone-200">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
                    {/* Disabled while the mailbox statuses load. Without this a
                        send fired in that window sees an empty option list, so
                        `sendFlags` is all-false and the routing falls through to
                        log-only — the message is recorded but never sent, with
                        nothing on screen saying so. The label stays "Send" while
                        loading rather than flashing "Log Email" and correcting
                        itself a moment later. */}
                    {/* #970 — a signature-only body is not a message: Send stays
                        off until something is typed (hasAuthoredText ignores
                        the signature block). */}
                    <Button
                        onClick={send}
                        disabled={sending || sendersLoading || !toAddress || !authored}
                        title={authored ? undefined : 'Type a message first'}
                        data-testid="send-email-button"
                    >
                        {sending || sendersLoading
                            ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                            : <Send className="w-4 h-4 mr-1.5" />}
                        {!sendersLoading && !hasAnySender ? 'Log Email' : 'Send'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
